import { request } from './client';
import type {
  Novel,
  Chapter,
  CrawlProgress,
  ExportData,
  SyncChaptersResult,
} from '../types';

export const novelApi = {
  /**
   * Create a new novel from URL
   */
  create(url: string): Promise<Novel> {
    return request<Novel>('/novels', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  /**
   * Get all novels
   */
  list(): Promise<Novel[]> {
    return request<Novel[]>('/novels');
  },

  /**
   * Get random novels for homepage
   */
  getRandom(limit: number = 10): Promise<Novel[]> {
    return request<Novel[]>(`/novels/random?limit=${limit}`);
  },

  /**
   * Search novels by query
   */
  search(query: string, limit: number = 50): Promise<Novel[]> {
    return request<Novel[]>(`/novels/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  },

  /**
   * Get novels by author name
   */
  getByAuthor(authorName: string): Promise<Novel[]> {
    return request<Novel[]>(`/novels/author/${encodeURIComponent(authorName)}`);
  },

  /**
   * Get a single novel by ID
   */
  get(id: string): Promise<Novel> {
    return request<Novel>(`/novels/${id}`);
  },

  /**
   * Delete a novel
   */
  delete(id: string): Promise<void> {
    return request<void>(`/novels/${id}`, { method: 'DELETE' });
  },

  /**
   * Pause novel crawling
   */
  pause(id: string): Promise<Novel> {
    return request<Novel>(`/novels/${id}/pause`, { method: 'POST' });
  },

  /**
   * Resume novel crawling
   */
  resume(id: string): Promise<Novel> {
    return request<Novel>(`/novels/${id}/resume`, { method: 'POST' });
  },

  /**
   * Recrawl empty chapters
   */
  recrawlEmpty(id: string): Promise<Novel & { recheduledCount: number }> {
    return request<Novel & { recheduledCount: number }>(`/novels/${id}/recrawl-empty`, {
      method: 'POST',
    });
  },

  /**
   * Sync chapters from source
   */
  syncChapters(id: string, dryRun = true): Promise<SyncChaptersResult> {
    return request<SyncChaptersResult>(`/novels/${id}/sync-chapters`, {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    });
  },

  /**
   * Get crawl progress
   */
  getProgress(id: string): Promise<CrawlProgress> {
    return request<CrawlProgress>(`/novels/${id}/progress`);
  },

  /**
   * Export novel data
   */
  export(id: string): Promise<ExportData> {
    return request<ExportData>(`/novels/${id}/export`);
  },

  /**
   * Check if novel is favorited
   */
  checkFavorite(id: string): Promise<{ isFavorited: boolean }> {
    return request<{ isFavorited: boolean }>(`/novels/${id}/favorite`);
  },

  /**
   * Recrawl a specific chapter
   */
  recrawlChapter(chapterId: string): Promise<Chapter & { message: string }> {
    return request<Chapter & { message: string }>(`/chapters/${chapterId}/recrawl`, {
      method: 'POST',
    });
  },

  /**
   * Skip a chapter (mark as manually skipped, let novel finalise)
   */
  skipChapter(chapterId: string): Promise<Chapter & { message: string }> {
    return request<Chapter & { message: string }>(`/chapters/${chapterId}/skip`, {
      method: 'POST',
    });
  },
};
